import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ClientError } from '@chainlit/react-client';

import { CenteredSplit } from '@/components/login/variants/CenteredSplit';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from 'client-types/*';

const SIGNUPS_CLOSED_CODE = 'signupsclosed';

export const LoginError = new Error(
  'Error logging in. Please try again later.'
);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const [signupsClosedOpen, setSignupsClosedOpen] = useState(false);
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw LoginError;
    setUserFromAPI();
  };

  const handleAuth = async (
    jsonPromise: Promise<any>,
    redirectURL?: string
  ) => {
    try {
      const json = await jsonPromise;
      handleCookieAuth(json);
      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      if (error instanceof ClientError && error.detail) {
        setError(error.detail);
      } else {
        setError(error.message);
      }
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();
    await handleAuth(jsonPromise, '/');
  };

  const handlePasswordLogin = async (email: string, password: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const jsonPromise = apiClient.passwordAuth(formData);
    await handleAuth(jsonPromise);
  };

  useEffect(() => {
    const queryError = query.get('error') || '';
    if (queryError.toLowerCase() === SIGNUPS_CLOSED_CODE) {
      setSignupsClosedOpen(true);
      setError('');
    } else {
      setError(queryError);
    }
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (config.headerAuth && !user) {
      handleHeaderAuth();
    }
    if (user) {
      navigate('/');
    }
  }, [config, user]);

  const variantProps = {
    error,
    providers: config?.oauthProviders || [],
    onPasswordSignIn: config?.passwordAuth ? handlePasswordLogin : undefined,
    onOAuthSignIn: async (provider: string) => {
      window.location.href = apiClient.getOAuthEndpoint(provider);
    }
  };

  return (
    <div className="dark relative min-h-svh w-full overflow-hidden bg-background text-foreground">
      <CenteredSplit {...variantProps} />

      <AlertDialog open={signupsClosedOpen} onOpenChange={setSignupsClosedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign-ups are closed</AlertDialogTitle>
            <AlertDialogDescription>
              New sign-ups are currently closed. If you already have an account,
              try signing in again, or contact an administrator for access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSignupsClosedOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
